import { Avatar, Badge, Button, Dropdown, Flex, Input, theme } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftOutlined, SendOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import SocketService from '../../../service/SocketService';
import ChatAPI from '../../../restAPI/ChatAPI';
import { retryApiCall, RETRY_CONFIGS } from '../../../utils/retryUtils';
import { clearChatMessages } from '../../../redux/slices/meetingChatSlice';

const { TextArea } = Input;

export default function MeetingChatScreen({ chat, userId, onBack }) {
    const dispatch = useDispatch();
    const chatContainerRef = useRef(null);
    const meetingChats = useSelector(state => state.meetingChat.chats);
    const [newMessage, setNewMessage] = useState('');
    const [conversation, setConversation] = useState({});

    const {
        token: {
            colorBgContainer,
            borderRadiusLG,
            colorPrimary,
            colorPrimaryBorder,
            colorTextDescription,
            colorTextLightSolid,
            fontSizeSM,
            colorBorder
        },
    } = theme.useToken();

    useEffect(() => {
        if (chat >= 0 && Array.isArray(meetingChats) && meetingChats.length > 0) {
            const foundConversation = meetingChats.find(item => item.id == chat);
            if (foundConversation) {
                setConversation(foundConversation);
                            
                try {
                    const draftKey = `meetingChatDraft_${chat}_${userId}`;
                    const savedDraft = localStorage.getItem(draftKey);
                    if (savedDraft) {
                        setNewMessage(savedDraft);
                    } else {
                        setNewMessage('');
                    }
                } catch (e) {
                    console.warn('Failed to load draft from localStorage:', e);
                    setNewMessage('');
                }
            }
        }
    }, [chat, meetingChats, userId]);

    
    const messages = conversation?.messages || [];

    useEffect(() => {        
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, conversation]);

    const handleSendMessage = () => {
        if (!newMessage.trim() || !conversation?.id) {
            return;
        }

        const message = {
            sender: { id: userId },
            text: newMessage,
            createdAt: new Date().toISOString(),
        };

        SocketService.sendMessage(conversation.id, message);
        setNewMessage('');
                
        try {
            localStorage.removeItem(`meetingChatDraft_${conversation.id}_${userId}`);
        } catch (e) {
            console.warn('Failed to clear draft from localStorage:', e);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleDeleteMessage = (messageId) => {
        if (!conversation?.id || !messageId) return;
        SocketService.deleteMessage(conversation.id, messageId);
    };

    const items = [
        {
            key: 'delete',
            label: 'Delete',
        },
    ];

    const avatarStyle = {
        backgroundColor: colorPrimaryBorder,
        color: colorPrimary,
    };

    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 96px)',
        width: '100%'
    };

    const headerStyle = {
        flex: '0 0 auto',
        padding: '8px 16px',
        background: colorBgContainer,
        zIndex: 1000,
        borderBottom: `1px solid ${colorBorder}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    };

    const chatContainerStyle = {
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: '8px 16px',
    };

    const textContainerBySenderStyle = {
        margin: 8,
        padding: 12,
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
    };

    const textContainerByReceiverStyle = {
        margin: 8,
        padding: 12,
        background: colorPrimary,
        color: colorTextLightSolid,
        borderRadius: borderRadiusLG,
    };

    const footerStyle = {
        flex: '0 0 auto',
        padding: '8px 16px',
        background: colorBgContainer,
        zIndex: 1000,
    };

    const footerTextAreaContainerStyle = {
        padding: 12,
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        width: '100%',
    };

    const chatTextSubtitleStyle = {
        fontSize: fontSizeSM,
        color: colorTextDescription,
        marginTop: 8
    };

    const chatMessageContentStyle = {
        display: 'block',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    };

    const chatUserStyle = {
        display: 'block',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    };

    const renderMessages = () => {
        return messages.map((message) => {
            const sender = conversation?.UserDetails?.find((member) => member.id === message.sender?.id || message.senderId);
            const isUser = (message.sender?.id || message.senderId) === userId;
            return (
                <Flex key={message.id} align={isUser ? 'end' : 'start'} justify={isUser ? 'flex-end' : 'flex-start'} gap={16} style={{ marginTop: 8 }}>
                    {!isUser && (
                        <div style={{ width: 32 }}>
                            <Avatar size={48} style={avatarStyle}>
                                {sender?.name?.charAt(0) || 'U'}
                            </Avatar>
                        </div>
                    )}
                    <div>
                        <Flex align={isUser ? 'end' : 'start'} vertical={true} style={isUser ? textContainerByReceiverStyle : textContainerBySenderStyle}>
                            <span style={chatMessageContentStyle}>{message.text}</span>
                            <span style={chatTextSubtitleStyle}>
                                {isUser ? (
                                    <>
                                        {message.createdAt ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}{' '}
                                        <Dropdown
                                            menu={{ items: items.map((item) => ({ ...item, onClick: () => handleDeleteMessage(message.id) })) }}
                                            trigger={['click']}
                                        >
                                            <DownOutlined />
                                        </Dropdown>
                                    </>
                                ) : (
                                    <>
                                        {sender?.name || 'Unknown'} • {message.createdAt ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </>
                                )}
                            </span>
                        </Flex>
                    </div>
                </Flex>
            );
        });
    };

    const clearChat = async () => {
        if (!conversation?.id || !userId) return;
        
        const payload = { chatId: conversation.id, userId };
        try {
            await retryApiCall(() => ChatAPI.postClearGroupChat(payload), RETRY_CONFIGS.USER_ACTION);            
            dispatch(clearChatMessages({ groupId: conversation.id }));
            console.log('Chat cleared successfully');
        } catch (error) {
            console.error('Failed to clear chat:', error);
        }
    };

    const headerDisplay = conversation?.name && conversation?.UserDetails
        ? { name: conversation.name, designation: conversation.UserDetails.map(member => member.name).join(', ') }
        : null;

    return (
        <div style={containerStyle}>
            {headerDisplay && (
                <div style={headerStyle}>
                    <Flex align='center' justify='space-between'>
                        <Flex align='center' justify='flex-start' gap={16}>
                            <Button icon={<ArrowLeftOutlined />} type='text' onClick={onBack} />
                            <div style={{ height: 48, width: 48 }}>
                                <Avatar size={48} style={avatarStyle}>
                                    {headerDisplay.name?.charAt(0)}
                                </Avatar>
                            </div>
                            <Flex align='' vertical={true}>
                                <span style={chatUserStyle}>{headerDisplay.name}</span>
                                {headerDisplay.designation && (
                                    <span style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
                                        {headerDisplay.designation}
                                    </span>
                                )}
                            </Flex>
                        </Flex>
                        <Button onClick={clearChat} icon={<DeleteOutlined />} type='text' />
                    </Flex>
                </div>
            )}
            <div style={chatContainerStyle} ref={chatContainerRef}>
                {renderMessages()}
            </div>
            <div style={footerStyle}>
                <Flex align='center' justify='space-between' gap={16}>
                    <div style={footerTextAreaContainerStyle}>
                        <TextArea
                            value={newMessage}
                            onKeyPress={handleKeyPress}
                            onChange={(e) => {
                                const msg = e.target.value;
                                setNewMessage(msg);                                
                                if (conversation?.id && userId) {
                                    try {
                                        const draftKey = `meetingChatDraft_${conversation.id}_${userId}`;
                                        if (msg && msg.trim()) {
                                            localStorage.setItem(draftKey, msg);
                                        } else {
                                            localStorage.removeItem(draftKey);
                                        }
                                    } catch (e) {
                                        console.warn('Failed to save draft to localStorage:', e);
                                    }
                                }
                            }}
                            rows={1}
                            placeholder="Type Your Message Here"
                            variant="borderless"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <Button
                        onClick={handleSendMessage}
                        icon={<SendOutlined />}
                        type='primary'
                        shape='circle'
                        size='large'
                        disabled={!newMessage.trim()}
                    />
                </Flex>
            </div>
        </div>
    );
}

